import * as Sentry from '@sentry/nextjs';
import {put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {AnalyticsEvent} from '../types/analytics';
import * as actions from '../actions/analyticsActions';
import {AuthUser} from 'redux/types/auth';
import AnalyticsAPI from 'api/Analytics';
import {ConsentCategory, hasConsent} from 'utils/consent';
import {getAnonymousSessionId, maySendAnonymousEvent} from 'utils/anonymousAnalytics';
import {
    getClientBrowser,
    getClientDevice,
    getClientOS,
    getClientScreenHeight,
    getClientScreenWidth,
    getBrowserVersion,
    getDeviceModel,
} from './../../utils/browserInformation';
import * as analyticsActionTypes from '../actions/analyticsActionTypes';
import * as authActionTypes from '../actions/authActionTypes';

class AnalyticsTrackError extends Error {
    public payload?: {event: AnalyticsEvent; data: any};
}

function getDeviceTracking() {
    const clientDevice = {
        os: '',
        browser: '',
        device: '',
        device_model: [''],
        screen_height: 0,
        screen_width: 0,
        browser_version: '',
    };
    if (typeof window !== 'undefined' && !!window?.navigator && !clientDevice.screen_height) {
        clientDevice.os = getClientOS();
        clientDevice.browser = getClientBrowser(navigator.userAgent, navigator.vendor, window.opera);
        clientDevice.browser_version = getBrowserVersion().version;
        clientDevice.device = getClientDevice(navigator.userAgent);
        clientDevice.device_model = getDeviceModel();
        clientDevice.screen_height = getClientScreenHeight();
        clientDevice.screen_width = getClientScreenWidth();
    }
    return clientDevice;
}

const CLIENT_DEVICE = getDeviceTracking();

function* track({payload}: Action<{event: AnalyticsEvent; distinct_id?: string; data: any}>) {
    // Nothing reaches the analytics pipeline until the visitor has granted the analytics
    // category. Termly's consent mode decides what granting means — every region is opt-in
    // today, so nothing is sent until someone accepts; switching the dashboard to opt-out
    // changes this without a code change, the same way the walkthrough works.
    //
    // hasConsent returns false when Termly has not loaded yet, so the earliest events in a
    // session are dropped rather than sent on an unknown state. Withholding is the safe
    // default and matches the walkthrough.
    if (!hasConsent(ConsentCategory.analytics)) {
        yield trackAnonymous(payload.event, payload.data?.abflags);
        return;
    }

    try {
        const user: AuthUser = yield select((state) => state.auth.user);
        yield trackAPI(payload.event, {
            ...payload.data,
            // use distinct_id if it's included in the payload, for unauthed users
            distinct_id: payload.distinct_id,
            // No identification-type flags here. ssn, permanent_resident_number, us_visa and
            // taxpayer_identification_number were attached to every authenticated event, so
            // which document an applicant holds — a direct proxy for citizenship and
            // immigration status — travelled with all of them. Nothing read them back; their
            // only consumer was the analytics stream.
            ...(user.id
                ? {
                      distinct_id: user.email,
                      user_id: user.id,
                      user_email: user.email,
                      version: process.env.NEXT_PUBLIC_STAGE,
                  }
                : {}),
            $os: CLIENT_DEVICE.os,
            $browser: CLIENT_DEVICE.browser,
            $browser_version: CLIENT_DEVICE.browser_version,
            $device: CLIENT_DEVICE.device,
            $device_model: CLIENT_DEVICE.device_model,
            $screen_height: CLIENT_DEVICE.screen_height,
            $screen_width: CLIENT_DEVICE.screen_width,
        });
        yield put(actions.trackSuccess(payload.event, payload.data));
    } catch (e) {
        (e as AnalyticsTrackError).payload = payload;
        Sentry.captureException(e);
        yield put(actions.trackFailed(e));
    }
}

function* trackAuthenticateViaToken() {
    yield put(actions.track(AnalyticsEvent.LOGIN_LINK_SUCCESS));
}

/**
 * The coarse counterpart to the identified stream, for visitors who have not answered the
 * banner. Only the event name travels — the rich payload above is dropped entirely rather
 * than filtered, so nothing added to an event later can leak through here.
 *
 * Event names were collapsed earlier so none of them encodes an answer: the prescribed
 * yes/no pair became one name, and the SSN/ITIN/visa/permanent-resident views became a single
 * identity-document name. Without that, the name alone would disclose what the payload does not.
 */
function* trackAnonymous(event: AnalyticsEvent, abflags: Record<string, boolean | string> = {}) {
    if (!maySendAnonymousEvent()) return;

    const distinctId = getAnonymousSessionId();
    if (!distinctId) return;

    try {
        // Only the flags travel from the identified payload. Everything else it carries is the
        // event's own data, which is exactly what must not leave before a decision.
        //
        // doCall swallows request failures and resolves with {status: false} rather than
        // rejecting, so the catch below never sees them — the status has to be read, or a
        // pre-consent funnel that has stopped reaching the backend looks identical to one
        // nobody is triggering.
        const result: {status?: boolean} = yield AnalyticsAPI.LogAnonymousEvent(event, distinctId, abflags);

        if (result?.status === false) {
            Sentry.captureMessage(`Pre-consent event was not accepted: ${event}`);
        }
    } catch (e) {
        // Anything that throws before the request is made. Either way this stays non-throwing:
        // a failed pre-consent event is not worth interrupting the session for.
        Sentry.captureException(e);
    }
}

const trackAPI = async (event: AnalyticsEvent, properties: any) => {
    const useSqsLogging = properties.useSqsLogging;
    const logData = properties;
    delete logData.useSqsLogging;

    await AnalyticsAPI.LogEvent(event, logData, {useSqsLogging});
};

export default function* analyticsSaga() {
    yield takeEvery(analyticsActionTypes.ANALYTICS_TRACK, track);
    yield takeLatest(authActionTypes.AUTH_AUTHENTICATE_VIA_TOKEN_SUCCESS, trackAuthenticateViaToken);
}
